import { Box, CircularProgress, Drawer, Typography } from '@mui/material';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchGlobalCatalogItemBySku } from 'entities/CatalogItem';
import { PropertyCatalogItemDetailed } from 'shared/ui/PropertyCatalogItemDetailed/PropertyCatalogItemDetailed';

import {
    mapCatalogItemToDisplayItem,
    mapGlobalCatalogItemToDisplayItem,
} from '../model/lib/mapProductToDisplayItem';

import type {
    CatalogItem,
    DisplayItem,
    GlobalCatalogItem,
} from 'entities/CatalogItem/model/types/CatalogItemSchema';

interface ProductDetailsDrawerProps {
    open: boolean;
    onClose: () => void;
    catalogItem?: CatalogItem | null;
    globalItem?: GlobalCatalogItem | null;
    sku?: string | null;
}

export const ProductDetailsDrawer = memo(
    ({
        open,
        onClose,
        catalogItem = null,
        globalItem = null,
        sku = null,
    }: ProductDetailsDrawerProps) => {
        const { t } = useTranslation();
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState<string | null>(null);
        const [loadedItem, setLoadedItem] = useState<GlobalCatalogItem | null>(
            null,
        );
        const [trackedSku, setTrackedSku] = useState<string | null>(null);
        const shouldLoadBySku =
            open && !catalogItem && !globalItem && Boolean(sku);
        const activeSku = shouldLoadBySku ? sku : null;

        if (trackedSku !== activeSku) {
            setTrackedSku(activeSku);
            setLoadedItem(null);
            setError(null);
            setLoading(Boolean(activeSku));
        }

        useEffect(() => {
            if (!activeSku) {
                return;
            }

            let isCancelled = false;

            const fetchItem = async () => {
                try {
                    const item = await fetchGlobalCatalogItemBySku(activeSku);
                    if (isCancelled) {
                        return;
                    }
                    if (item) {
                        setLoadedItem(item);
                        setError(null);
                    } else {
                        setLoadedItem(null);
                        setError('failed');
                    }
                } catch {
                    if (!isCancelled) {
                        setLoadedItem(null);
                        setError('failed');
                    }
                } finally {
                    if (!isCancelled) {
                        setLoading(false);
                    }
                }
            };

            fetchItem();

            return () => {
                isCancelled = true;
            };
        }, [activeSku]);

        const displayItem: DisplayItem | null = useMemo(() => {
            if (catalogItem) {
                return mapCatalogItemToDisplayItem(catalogItem);
            }
            if (globalItem) {
                return mapGlobalCatalogItemToDisplayItem(globalItem);
            }
            if (loadedItem && trackedSku === activeSku) {
                return mapGlobalCatalogItemToDisplayItem(loadedItem);
            }
            return null;
        }, [activeSku, catalogItem, globalItem, loadedItem, trackedSku]);

        const detailId =
            catalogItem?.id || globalItem?.uid || loadedItem?.uid || sku || '';

        const detailPrice = catalogItem?.price ?? 0;

        return (
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                sx={{
                    zIndex: (theme) => theme.zIndex.modal + 2,
                }}
                PaperProps={{
                    sx: {
                        width: 480,
                        maxWidth: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100vh',
                    },
                }}
                ModalProps={{
                    BackdropProps: {
                        invisible: true,
                    },
                }}
            >
                <Box sx={{ flex: 1, padding: 2, minHeight: 0 }}>
                    {shouldLoadBySku && loading && (
                        <Box
                            sx={{
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CircularProgress size={24} />
                        </Box>
                    )}
                    {shouldLoadBySku && !loading && error && (
                        <Typography color="error" variant="body2">
                            {t('Unknown Error')}
                        </Typography>
                    )}
                    {!(shouldLoadBySku && (loading || error)) &&
                        displayItem && (
                            <PropertyCatalogItemDetailed
                                displayItem={displayItem}
                                onCancel={onClose}
                                price={detailPrice}
                                id={detailId}
                                isOrderPage={false}
                                moveTo={undefined}
                                minAllowedInStock={0}
                                isLocal={Boolean(catalogItem?.id)}
                                hideMinOnHand
                                headerMode="close-only"
                            />
                        )}
                </Box>
            </Drawer>
        );
    },
);

ProductDetailsDrawer.displayName = 'ProductDetailsDrawer';
